import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  ProEntitlement,
  SessionsResponse,
  SessionDetail,
  VehiclesResponse,
  VehicleDetail,
  ReportsResponse,
  ReportDownload,
  ShopProfile,
  ShopProfileUpdate,
  LogoUrlResponse,
  CommLogJsonResponse,
  NhtsaVinDecodeResult,
  VinDecodeResult,
  MarketValueData,
  TitleCheckData,
  AuctionData,
  WarrantyData,
  MaintenanceData,
  CommonRepairsData,
  SalesHistoryData,
  VehicleSummaryResponse,
  GenerateReportRequest,
  GenerateReportResponse,
  RefineReportRequest,
  RefineReportResponse,
  FinalizeReportRequest,
} from '@/types';

/** Thrown when the user is signed in but has no plan that unlocks the dashboard. Not an auth failure. */
export class SubscriptionRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SubscriptionRequiredError';
  }
}

/** Thrown by the API client when a request could not be authenticated (no token / 401). */
export class AuthRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthRequiredError';
  }
}


// API base URL - use environment variable in production
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
  'https://obdaiazurefunctions20240928071526-uat.azurewebsites.net/api';

class ProApiClient {
  private client: AxiosInstance;
  private getAccessToken: (() => Promise<string>) | null = null;
  private onAuthRequired: ((reason: string) => void) | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Auth interceptor. A stale/expired Auth0 session used to be swallowed here: the request went
    // out WITHOUT a bearer token, the API answered 401, react-query surfaced "no data", and pages
    // rendered the "Premium Subscription Required" paywall to a paying customer (#164). Now an
    // unobtainable token never reaches the API: it fails the request as an auth problem and hands
    // the app back to the login flow.
    this.client.interceptors.request.use(async (config) => {
      if (this.getAccessToken) {
        try {
          const token = await this.getAccessToken();
          config.headers.Authorization = `Bearer ${token}`;
        } catch (error) {
          console.error('Failed to get access token:', error);
          this.onAuthRequired?.('token');
          throw new AuthRequiredError('Your session has expired. Please sign in again.');
        }
      }
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error instanceof AxiosError && error.response?.status === 401) {
          // The PRO endpoints answer 401 for "signed in, but no qualifying plan" as well as for a
          // dead token. Only the second is an auth problem. Treating the first as one re-ran
          // sign-in, got the same 401, and signed a free user out instead of showing the paywall.
          const body = error.response.data as { message?: string; Message?: string } | undefined;
          if (/subscription required/i.test(body?.message ?? body?.Message ?? '')) {
            return Promise.reject(new SubscriptionRequiredError('A Premium or PRO subscription is required.'));
          }
          this.onAuthRequired?.('401');
          return Promise.reject(new AuthRequiredError('Your session has expired. Please sign in again.'));
        }
        return Promise.reject(error);
      },
    );
  }

  setTokenGetter(getter: () => Promise<string>) {
    this.getAccessToken = getter;
  }

  /** Called once per auth failure (unobtainable token or API 401); the auth layer re-runs login. */
  setAuthRequiredHandler(handler: (reason: string) => void) {
    this.onAuthRequired = handler;
  }

  // Entitlement
  async getAriaBalance(): Promise<import('@/types/ariaAccounting').AriaBalanceReply> {
    const { data } = await this.client.post<import('@/types/ariaAccounting').AriaBalanceReply>(
      '/aria/accounting/balance', { protocol: 'purchased-v1' });
    return data;
  }

  async getEntitlement(): Promise<ProEntitlement> {
    // Ultra-aware build: without this contract the API reports Ultra as 'pro' for older dashboards.
    const { data } = await this.client.get<ProEntitlement>('/pro/entitlement', { params: { planContract: 'ultra-1' } });
    return data;
  }

  // Sessions
  async getSessions(params?: {
    page?: number;
    pageSize?: number;
    vin?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<SessionsResponse> {
    const { data } = await this.client.get<SessionsResponse>('/pro/sessions', { params });
    return data;
  }

  async getSession(key: string): Promise<SessionDetail> {
    const { data } = await this.client.get<SessionDetail>(`/pro/sessions/${key}`);
    return data;
  }

  // Vehicles
  async getVehicles(): Promise<VehiclesResponse> {
    const { data } = await this.client.get<VehiclesResponse>('/pro/vehicles');
    return data;
  }

  async getVehicle(vin: string): Promise<VehicleDetail> {
    const { data } = await this.client.get<VehicleDetail>(`/pro/vehicles/${vin}`);
    return data;
  }

  // Reports
  async getReports(vin?: string): Promise<ReportsResponse> {
    const { data } = await this.client.get<ReportsResponse>('/pro/reports', {
      params: vin ? { vin } : undefined,
    });
    return data;
  }

  async getReportDownload(reportId: string): Promise<ReportDownload> {
    const { data } = await this.client.get<ReportDownload>(`/pro/reports/${reportId}/download`);
    return data;
  }

  // Shop Profile
  async getShopProfile(): Promise<ShopProfile> {
    const { data } = await this.client.get<ShopProfile>('/pro/shop-profile');
    return data;
  }

  async updateShopProfile(profile: ShopProfileUpdate): Promise<ShopProfile> {
    const { data } = await this.client.put<ShopProfile>('/pro/shop-profile', profile);
    return data;
  }

  async getShopLogoUrl(): Promise<LogoUrlResponse> {
    const { data } = await this.client.get<LogoUrlResponse>('/pro/shop-profile/logo');
    return data;
  }

  // Rich session data. Both go through the authenticated /pro routes, which only serve sessions
  // the signed-in user owns. They used to call the key-protected functions directly, which put a
  // function key in every browser and would return any VIN's data to whoever held it (#106).
  async getCommLogJson(_vin: string, sessionKey?: string): Promise<CommLogJsonResponse> {
    if (!sessionKey) throw new Error('getCommLogJson needs a sessionKey');
    const { data } = await this.client.get<CommLogJsonResponse>(
      `/pro/sessions/${encodeURIComponent(sessionKey)}/commlog`
    );
    return data;
  }

  /** Time-series telemetry as JSONL. `opts` picks the window and resolution (#112). */
  async getSecondBySecondData(
    sessionKey: string,
    opts?: { maxPoints?: number; from?: string | number; to?: string | number; agg?: 'last' | 'minmax' }
  ): Promise<string> {
    const { data } = await this.client.get<string>(
      `/pro/sessions/${encodeURIComponent(sessionKey)}/second-by-second`,
      { params: opts, responseType: 'text', transformResponse: [(raw) => raw] }
    );
    return data;
  }

  // NHTSA Free VIN Decode (auto-called on session detail)
  async decodeVinNhtsa(vin: string): Promise<NhtsaVinDecodeResult> {
    console.debug('[NHTSA VinDecode] Fetching for VIN:', vin);
    const { data } = await this.client.get<NhtsaVinDecodeResult>(`/nhtsa-vin-decode/${vin}`);
    return data;
  }

  // VehicleDatabases API endpoints (Premium - paid)
  async decodeVin(vin: string): Promise<VinDecodeResult> {
    console.debug('[VinDecode Premium] Fetching for VIN:', vin);
    const { data } = await this.client.get<VinDecodeResult>(`/vin-decode/${vin}`);
    return data;
  }

  // Helper to handle 400 responses as "no data" for VehicleDatabases APIs
  private async getWithNoDataHandling<T extends { status: string; data?: unknown }>(
    url: string
  ): Promise<T> {
    try {
      const { data } = await this.client.get<T>(url);
      return data;
    } catch (error) {
      // VehicleDatabases.com returns 400 when no records exist for a VIN
      if (error instanceof AxiosError && error.response?.status === 400) {
        console.debug('[API] 400 response - no data for:', url);
        return { status: 'no_data', data: undefined } as T;
      }
      throw error;
    }
  }

  async getMarketValue(vin: string): Promise<MarketValueData> {
    console.debug('[MarketValue] Fetching for VIN:', vin);
    return this.getWithNoDataHandling<MarketValueData>(`/market-value/${vin}`);
  }

  async getTitleCheck(vin: string): Promise<TitleCheckData> {
    console.debug('[TitleCheck] Fetching for VIN:', vin);
    return this.getWithNoDataHandling<TitleCheckData>(`/title-check/${vin}`);
  }

  async getAuction(vin: string): Promise<AuctionData> {
    console.debug('[Auction] Fetching for VIN:', vin);
    return this.getWithNoDataHandling<AuctionData>(`/auction/${vin}`);
  }

  async getWarranty(year: string, make: string, model: string): Promise<WarrantyData> {
    console.debug('[Warranty] Fetching for:', year, make, model);
    const { data } = await this.client.get<WarrantyData>(
      `/vehicle-warranty/${encodeURIComponent(year)}/${encodeURIComponent(make)}/${encodeURIComponent(model)}`
    );
    return data;
  }

  async getMaintenance(vin: string): Promise<MaintenanceData> {
    console.debug('[Maintenance] Fetching for VIN:', vin);
    return this.getWithNoDataHandling<MaintenanceData>(`/vehicle-maintenance/${vin}`);
  }

  async getCommonRepairs(vin: string): Promise<CommonRepairsData> {
    console.debug('[Repairs] Fetching for VIN:', vin);
    return this.getWithNoDataHandling<CommonRepairsData>(`/vehicle-repairs/${vin}`);
  }

  async getSalesHistory(vin: string): Promise<SalesHistoryData> {
    console.debug('[SalesHistory] Fetching for VIN:', vin);
    return this.getWithNoDataHandling<SalesHistoryData>(`/sales-history/${vin}`);
  }

  // AI Vehicle Summary
  async getVehicleSummary(vin: string): Promise<VehicleSummaryResponse> {
    const { data } = await this.client.get<VehicleSummaryResponse>(
      `/pro/vehicles/${encodeURIComponent(vin)}/summary`
    );
    return data;
  }

  // Report Generation
  async generateReport(body: GenerateReportRequest): Promise<GenerateReportResponse> {
    const { data } = await this.client.post<GenerateReportResponse>(
      '/pro/reports/generate', body
    );
    return data;
  }

  // Report Refinement
  async refineReport(body: RefineReportRequest): Promise<RefineReportResponse> {
    const { data } = await this.client.post<RefineReportResponse>(
      '/pro/reports/refine', body
    );
    return data;
  }

  // Report Finalization
  async finalizeReport(body: FinalizeReportRequest): Promise<{ reportId: string; message: string }> {
    const { data } = await this.client.post<{ reportId: string; message: string }>(
      '/pro/reports/finalize', body
    );
    return data;
  }

  // Vehicle Image — returns a direct URL for use in <img src>
  getVehicleImageUrl(vin: string): string {
    return `${API_BASE_URL}/vehicle-image/${encodeURIComponent(vin)}`;
  }

  // Chat — Get full chat history for a session
  async getChatHistory(sessionKey: string): Promise<ChatHistoryResponse> {
    const { data } = await this.client.get<ChatHistoryResponse>(`/pro/chat/${sessionKey}/history`);
    return data;
  }

  // Chat — Stream a message to ARIA with optional file attachments
  async streamChatMessage(sessionKey: string, message: string, chatSessionId?: string, files?: File[]): Promise<Response> {
    const token = this.getAccessToken ? await this.getAccessToken() : '';
    // ARIA states dates in the user's own zone, the same one the pages render in (#153).
    const clientTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // The panel stays open while the user browses, so the session on screen can differ from the one
    // the conversation belongs to. ARIA is told which, so "this drive" means what they are looking at.
    const onScreen = window.location.pathname.match(/\/sessions\/([0-9a-fA-F-]{36})/)?.[1] ?? '';
    const viewingSessionKey = onScreen && onScreen.toLowerCase() !== sessionKey.toLowerCase() ? onScreen : '';

    if (files && files.length > 0) {
      const formData = new FormData();
      formData.append('message', message);
      formData.append('clientTimezone', clientTimezone);
      if (viewingSessionKey) formData.append('viewingSessionKey', viewingSessionKey);
      if (chatSessionId) formData.append('chatSessionId', chatSessionId);
      files.forEach(f => formData.append('files', f));

      return fetch(`${API_BASE_URL}/pro/chat/${sessionKey}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
    }

    return fetch(`${API_BASE_URL}/pro/chat/${sessionKey}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ message, chatSessionId, clientTimezone, viewingSessionKey: viewingSessionKey || undefined }),
    });
  }

  // Chat — Download attachment via SAS URL
  async getChatAttachmentUrl(attachmentId: string): Promise<string> {
    const { data } = await this.client.get<{ downloadUrl: string }>(`/pro/chat/attachments/${attachmentId}/download`);
    return data.downloadUrl;
  }
}

// Chat types
export interface ChatAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  downloadUrl?: string;
}

export interface ChatHistoryResponse {
  sessionKey: string;
  sessionCreatedAt?: string;   // UTC
  vehicle: { vin: string; year: string; make: string; model: string };
  messages: ChatHistoryMessage[];
}

export interface ChatHistoryMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  modelName?: string;
  source: 'mobile' | 'web';
  attachments?: ChatAttachment[];
}

export const apiClient = new ProApiClient();
